import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { Button } from '@/components/ui';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reportarCrashDeRender } from '@/shared/services/client-error-reporter';

/**
 * RouteErrorBoundary
 * ------------------
 * Boundary de NIVEL RUTA (no global). Aisla un crash a la pagina activa,
 * dejando navbar/sidebar/layout intactos. Sirve como segunda linea de defensa
 * por debajo de AppErrorBoundary (que sigue cubriendo errores globales tipo
 * crash en providers, layout, etc).
 *
 * Por que existe (gap-route-level-error-boundaries, audit 2026-06):
 *   - El dashboard usa 365+ chunks lazy-loaded (React.lazy).
 *   - Si una sola pagina lanza, AppErrorBoundary global cae a fallback de
 *     pantalla completa: en una demo en vivo con cliente eso = ratataplan.
 *   - Con boundary por ruta, el shell sigue navegable y el usuario solo ve
 *     una RouteCrashCard en el slot de contenido.
 *
 * Patron estandar React. Usamos componente clase porque la API oficial de
 * error boundaries (componentDidCatch + getDerivedStateFromError) sigue
 * siendo class-only en React 18.
 *
 * Logging:
 *   - Si Sentry esta cargado en window (window.Sentry?.captureException),
 *     loggea con tags { boundary: 'route', moduleName }.
 *   - Si no, console.error con prefijo identificable.
 *   - Mock-friendly: no hay dependencia hard a @sentry/react.
 */

interface RouteCrashCardProps {
  moduleName: string;
  error: Error;
  onRetry: () => void;
}

const RouteCrashCard: React.FC<RouteCrashCardProps> = ({
  moduleName,
  error,
  onRetry,
}) => {
  return (
    <div className="flex items-center justify-center p-6 min-h-[60vh]">
      <Card className="w-full max-w-2xl shadow-lg border-amber-200">
        <CardHeader className="bg-amber-50 border-b border-amber-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-xl font-bold text-amber-900">
              No pudimos cargar {moduleName}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <p className="text-gray-700">
            Ocurrió un error al mostrar este módulo. El resto de la
            aplicación sigue funcionando: puedes ir a otra sección desde
            el menú o intentar cargar este módulo de nuevo.
          </p>

          {/* Detalle tecnico colapsable — util para soporte / dev */}
          <details className="bg-gray-50 border border-gray-200 rounded p-3 text-sm">
            <summary className="cursor-pointer text-gray-600 font-medium">
              Detalles técnicos
            </summary>
            <div className="mt-2 font-mono text-xs text-gray-800 break-words whitespace-pre-wrap">
              {error.name}: {error.message}
            </div>
          </details>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              onClick={onRetry}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
            <Button
              onClick={() => (window.location.href = '/')}
              variant="outline"
              className="flex-1"
            >
              <Home className="h-4 w-4 mr-2" />
              Ir al inicio
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

interface RouteErrorBoundaryProps {
  moduleName: string;
  routePath?: string;
  /**
   * GH#2404 — si cambia mientras se muestra la tarjeta de error, el boundary se reinicia.
   * `envolverRuta` le pasa la ubicación (pathname): todas las rutas se montan en la misma
   * posición del árbol, así que React reutiliza ESTA instancia al navegar, y sin esto el
   * error de una pantalla se heredaba a todas las demás del menú.
   */
  resetKey?: string;
  children: React.ReactNode;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  resetKey?: string;
}

// Optional Sentry hook — no hard dep. Si Sentry esta instalado y montado
// en window.Sentry (init clasico) lo aprovechamos; si no, fallback console.
type SentryLike = {
  captureException?: (err: unknown, ctx?: Record<string, unknown>) => void;
};

class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: props.resetKey };
  }

  // GH#2404 — al cambiar la ubicación se limpia el error ANTES de pintar, no después en
  // componentDidUpdate (que pintaría primero la tarjeta con el nombre de la sección nueva).
  // Una página sana no se vuelve a montar: sólo se toca el estado si había error.
  static getDerivedStateFromProps(
    props: RouteErrorBoundaryProps,
    state: RouteErrorBoundaryState,
  ): Partial<RouteErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return state.hasError
      ? { hasError: false, error: null, resetKey: props.resetKey }
      : { resetKey: props.resetKey };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<RouteErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { moduleName, routePath } = this.props;

    // 1) Sentry si esta presente en window
    try {
      const sentry = (window as unknown as { Sentry?: SentryLike }).Sentry;
      if (sentry && typeof sentry.captureException === 'function') {
        sentry.captureException(error, {
          tags: {
            boundary: 'route',
            moduleName,
            routePath: routePath ?? 'unknown',
          },
          extra: {
            componentStack: errorInfo.componentStack,
          },
        });
      }
    } catch {
      // No-op: Sentry opcional, jamas tumbar boundary por logging fail
    }

    // 2) GH#1778 capa 4b — a la caja negra propia (no depende de Sentry)
    reportarCrashDeRender({
      error,
      componentStack: errorInfo.componentStack,
      boundary: 'route',
      moduleName,
      routePath,
    });

    // 3) Console fallback — siempre logueamos algo
    // eslint-disable-next-line no-console
    console.error(
      `[RouteErrorBoundary] crash en modulo "${moduleName}" ` +
        `(path=${routePath ?? 'unknown'}):`,
      error,
      errorInfo.componentStack,
    );
  }

  handleRetry = (): void => {
    // Reset boundary — re-monta children. Si el crash era transitorio
    // (chunk-load fail, race condition), esto recupera sin recargar todo.
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <RouteCrashCard
          moduleName={this.props.moduleName}
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
export { RouteCrashCard };
